/**
 * Field configurations and validation helpers for Sample Tags
 */

import { CountryListSelectInputOptions } from '../../../Lib/Constants/countries';
import {
  SampleDetailsField,
  SampleCreatedWithField,
  ItemFilters,
  DevelopmentType,
  AddTagFormData,
} from './types';

/**
 * Get allowed sample tag types based on vendor merch_type
 */
export const getAllowedSampleTypes = (merchType?: string): ItemFilters[] => {
  const normalizedMerchType = merchType?.toLowerCase().trim();

  switch (normalizedMerchType) {
    case 'direct':
      return [
        ItemFilters.PROTO_DEVELOPMENT,
        ItemFilters.FIT,
        ItemFilters.PRE_PRODUCTION,
        ItemFilters.TOP,
        ItemFilters.BLOCK,
      ];

    case '3rd party':
      return [
        ItemFilters.FIRST_SAMPLE,
        ItemFilters.PRE_PRODUCTION,
        ItemFilters.TOP,
      ];

    default:
      return [
        ItemFilters.FIRST_SAMPLE,
        ItemFilters.PROTO_DEVELOPMENT,
        ItemFilters.FIT,
        ItemFilters.PRE_PRODUCTION,
        ItemFilters.TOP,
        ItemFilters.BLOCK,
      ];
  }
};

export const FILE_VALIDATION = {
  ACCEPTED_TYPES: ['image/jpeg', 'image/jpg', 'image/png'] as const,
  MAX_SIZE: 5 * 1024 * 1024,
  MAX_SIZE_LABEL: '5MB',
} as const;

/**
 * Sample Details field configurations
 */
export const SAMPLE_DETAILS_FIELDS: SampleDetailsField[] = [
  {
    name: 'fnExclusive',
    label: 'FN Exclusive',
    options: [
      { label: 'No', value: false },
      { label: 'Yes', value: true },
    ],
  },
  {
    name: 'fitSample',
    label: 'Fit Sample',
    options: [
      { label: 'No', value: false },
      { label: 'Yes', value: true },
    ],
  },
];

/**
 * Sample Created With field configurations
 */
export const SAMPLE_CREATED_WITH_FIELDS: SampleCreatedWithField[] = [
  { name: 'fabricType', label: 'Fabric' },
  { name: 'trimType', label: 'Trim' },
  { name: 'labelType', label: 'Label' },
  { name: 'washColorType', label: 'Wash Color' },
];

/**
 * Field descriptions for Sample Details section
 */
export const SAMPLE_DETAILS_DESCRIPTIONS = {
  FN_EXCLUSIVE: 'FN Exclusive: Product is only being sold to Fashion Nova.',
  FIT_SAMPLE: 'Fit Sample: Can be used as a fit sample',
};

/**
 * Field descriptions for Sample Created With section
 */
export const SAMPLE_CREATED_WITH_DESCRIPTIONS = {
  AVAILABLE: 'Available: Used closest to the approved/original (OG) quality.',
  PRODUCTION: 'Production: Used bulk-approved quality.',
};

/**
 * Development Type Options for form select
 */
export const DEVELOPMENT_TYPE_OPTIONS = Object.values(DevelopmentType).map(
  (type) => ({
    label: type,
    value: type,
  }),
);

export const CLASS_AND_LOGISTICS_REQ_DEV_TYPES = [
  DevelopmentType.BUYER_REQUESTED,
  DevelopmentType.VENDOR_SAMPLE,
  DevelopmentType.LUXE,
];

export const PROTO_DEVELOPMENT_SECTIONS = {
  base: [
    'protoFields',
    'dateFields',
    'sampleCreatedWith',
    'selfShell',
    'additionalFabrics',
    'comments',
  ],
  withClassAndLogistics: ['productClassification', 'logistics'],
} as const;

/**
 * Possible parameters for filtering
 */
export const possibleParams = [
  {
    field: 'type_of_sample_tag',
    type: 'string' as const,
    values: [
      ItemFilters.FIRST_SAMPLE,
      ItemFilters.PROTO_DEVELOPMENT,
      ItemFilters.FIT,
      ItemFilters.PRE_PRODUCTION,
      ItemFilters.TOP,
      ItemFilters.BLOCK,
    ] as const,
  },
] as const;

/**
 * Helper function for country options based on eligibility
 */
export const getCountryOptions = (domesticMerchPoEligible?: boolean) => {
  if (domesticMerchPoEligible) {
    return CountryListSelectInputOptions;
  }
  return CountryListSelectInputOptions.filter(
    (option) => option.label !== 'United States',
  );
};

/**
 * Sample Type Configuration - defines which sections to show for each sample type
 */

const PO_BASED_SECTIONS = [
  'poAndProduct',
  'dateFields',
  'sampleCreatedWith',
  'selfShell',
  'additionalFabrics',
  'comments',
];

export const SAMPLE_TYPE_CONFIG = {
  [ItemFilters.FIRST_SAMPLE]: {
    sections: [
      'productImage',
      'productClassification',
      'costAndMoq',
      'logistics',
      'sampleDetails',
      'selfShell',
    ],
  },
  [ItemFilters.PROTO_DEVELOPMENT]: {
    sections: [
      'protoFields',
      'dateFields',
      'sampleCreatedWith',
      'selfShell',
      'additionalFabrics',
      'comments',
    ],
  },
  [ItemFilters.FIT]: {
    sections: PO_BASED_SECTIONS,
  },
  [ItemFilters.PRE_PRODUCTION]: {
    sections: PO_BASED_SECTIONS,
  },
  [ItemFilters.TOP]: {
    sections: PO_BASED_SECTIONS,
  },
  [ItemFilters.BLOCK]: {
    sections: [
      'productImage',
      'blockHeader',
      'dateFields',
      'blockSampleDetails',
      'sampleCreatedWith',
      'blockOtherAttributes',
      'comments',
    ],
  },
};

/**
 * Mapping of form field names to their section IDs for scroll-to-error functionality
 */
export const FIELD_TO_SECTION_MAP: Record<string, string> = {
  sampleType: 'section-sampleType',
  styleNumber: 'section-productImage',
  productImageUrl: 'section-productImage',
  division: 'section-productClassification',
  department: 'section-productClassification',
  category: 'section-productClassification',
  class: 'section-productClassification',
  poNumber: 'section-poAndProduct',
  selectedProduct: 'section-poAndProduct',
  poDueDate: 'section-poAndProduct',
  poLineName: 'section-poAndProduct',
  poLineColor: 'section-poAndProduct',
  poLineSize: 'section-poAndProduct',
  dateSent: 'section-dateFields',
  cutDate: 'section-dateFields',
  typeOfDevelopment: 'section-protoFields',
  styleName: 'section-protoFields',
  sampleSize: 'section-protoFields',
  colorWash: 'section-protoFields',
  fabricType: 'section-sampleCreatedWith',
  trimType: 'section-sampleCreatedWith',
  labelType: 'section-sampleCreatedWith',
  washColorType: 'section-sampleCreatedWith',
  customCutLeadTime: 'section-logistics',
  coo: 'section-logistics',
  shipmentMode: 'section-logistics',
  fnExclusive: 'section-sampleDetails',
  fitSample: 'section-sampleDetails',
  fiberContents: 'section-selfShell',
  fabricWeight: 'section-selfShell',
  fabricCodeName: 'section-selfShell',
  juniorCost: 'section-costAndMoq',
  juniorMoq: 'section-costAndMoq',
  plusCost: 'section-costAndMoq',
  plusMoq: 'section-costAndMoq',
  comments: 'section-comments',
  contrastSection: 'section-additionalFabrics',
  liningSection: 'section-additionalFabrics',
  fillingSection: 'section-additionalFabrics',
  coatingSection: 'section-additionalFabrics',
  backingSection: 'section-additionalFabrics',
  blockNumber: 'section-blockHeader',
  pantsAttribute: 'section-blockOtherAttributes',
  topsAttribute: 'section-blockOtherAttributes',
  selfShellFabricCode: 'section-blockSampleDetails',
};

/**
 * Base form values used for initialization
 */
export const BASE_FORM_VALUES: Partial<AddTagFormData> = {
  sampleType: ItemFilters.FIRST_SAMPLE,
  id: undefined,
};

/**
 * Default values for each form section
 */
export const SECTION_DEFAULT_VALUES: Record<string, Partial<AddTagFormData>> = {
  productImage: {
    styleNumber: '',
    productImageUrl: '',
    productImageFile: undefined,
  },
  productClassification: {
    division: '',
    department: '',
    category: '',
    class: '',
  },
  poAndProduct: {
    poNumber: '',
    selectedProduct: '',
    poDueDate: '',
    poLineName: '',
    poLineColor: '',
    poLineSize: '',
  },
  dateFields: {
    dateSent: '',
    cutDate: '',
  },
  protoFields: {
    typeOfDevelopment: undefined,
    productImageUrl: '',
    styleNumber: '',
    styleName: '',
    sampleSize: '',
    colorWash: '',
  },
  sampleCreatedWith: {
    fabricType: undefined,
    trimType: undefined,
    labelType: undefined,
    washColorType: undefined,
  },
  logistics: {
    customCutLeadTime: undefined,
    ccLeadTimeDate: '',
    coo: '',
    shipmentMode: '',
  },
  sampleDetails: {
    fnExclusive: undefined,
    fitSample: undefined,
  },
  selfShell: {
    fiberContents: [{ id: 'fiber-0', fiber: '', percent_of_fibers: 0 }],
    fabricWeight: '',
    fabricCodeName: '',
  },
  additionalFabrics: {
    contrastSection: undefined,
    liningSection: undefined,
    fillingSection: undefined,
    coatingSection: undefined,
    backingSection: undefined,
  },
  costAndMoq: {
    juniorCost: undefined,
    juniorMoq: undefined,
    plusCost: undefined,
    plusMoq: undefined,
  },
  comments: {
    comments: '',
  },
  blockHeader: {
    blockNumber: '',
    category: '',
  },
  blockSampleDetails: {
    sampleSize: '',
    selfShellFabricCode: '',
  },
  blockOtherAttributes: {
    pantsAttribute: undefined,
    topsAttribute: undefined,
  },
};

/**
 * Generate default form values for a specific sample type
 * @param sampleType - The sample type to generate defaults for
 * @returns Complete form data with defaults
 */
export const getDefaultFormValues = (
  sampleType: ItemFilters,
): AddTagFormData => {
  const config = SAMPLE_TYPE_CONFIG[sampleType];
  const defaults = { ...BASE_FORM_VALUES, sampleType };

  config.sections.forEach((section) => {
    if (SECTION_DEFAULT_VALUES[section]) {
      Object.assign(defaults, SECTION_DEFAULT_VALUES[section]);
    }
  });

  return defaults as AddTagFormData;
};
